import { useScopedOrganizationId } from "@/contexts/WorkspaceOrgContext";
import { OpsLookupOrderDetail, OpsLookupSaleDetail } from "@/components/ops/OpsLookupDetails";
import {
  OpsLookupRelatedServices,
  OpsLookupServiceDetail,
} from "@/components/ops/OpsLookupServiceDetail";
import {
  OpsLookupOrderList,
  OpsLookupSaleList,
  OpsLookupServiceList,
} from "@/components/ops/OpsLookupLists";
import { useOpsLookup } from "@/components/ops/useOpsLookup";
import { useOpsLookupCosts } from "@/components/ops/useOpsLookupCosts";
import { useOpsLookupLocations } from "@/components/ops/useOpsLookupLocations";
import type { MfgPlant } from "@/lib/manufacturing/types";
import {
  type OpsLookupSaleRow,
  type OpsLookupSlider,
  type OpsLookupUnitRow,
} from "@/lib/opsLookup/opsLookupModel";
import type { OpsLookupCostRollup } from "@/lib/opsLookup/opsLookupCosts";
import type { ReactNode } from "react";

const tabBtn = (active: boolean) =>
  `rounded-md px-3 py-1.5 text-sm font-medium ${
    active
      ? "bg-slate-800 text-white dark:bg-zinc-200 dark:text-zinc-900"
      : "border border-gray-300 text-gray-800 hover:bg-gray-50 dark:border-zinc-600 dark:text-zinc-200 dark:hover:bg-zinc-800"
  }`;

function OpsLookupOrderDetailConnected(props: {
  unit: OpsLookupUnitRow;
  plant: MfgPlant | null;
  linkedSale: OpsLookupSaleRow | null;
  cost: OpsLookupCostRollup;
  costLoading: boolean;
  relatedServices?: ReactNode;
  onOpenSale: (dealId: string) => void;
}) {
  const locations = useOpsLookupLocations({ unit: props.unit, plant: props.plant });
  return (
    <OpsLookupOrderDetail
      unit={props.unit}
      plantName={props.plant?.name ?? props.plant?.plant_code ?? null}
      linkedSale={props.linkedSale}
      cost={props.cost}
      costLoading={props.costLoading}
      locationTriad={locations.triad}
      locationsLoading={locations.loading}
      relatedServices={props.relatedServices}
      onOpenSale={props.onOpenSale}
    />
  );
}

const SLIDER_LABEL: Record<OpsLookupSlider, string> = {
  sales: "Sales",
  orders: "Orders",
  service: "Service",
};

/**
 * Ops → Lookup (OL-1…OL-5): Sales / Orders / Service find surface.
 * Read-only. See docs/planning/ops_lookup_mode.md.
 */
export function OpsLookupPanel() {
  const organizationId = useScopedOrganizationId();
  const vm = useOpsLookup(organizationId);

  const focusedDealId =
    vm.slider === "sales"
      ? vm.selectedSaleId
      : vm.slider === "orders"
        ? (vm.selectedOrder?.ops_deal_id ?? null)
        : null;
  const costs = useOpsLookupCosts(focusedDealId);

  if (!organizationId) {
    return (
      <div data-app-panel="OpsLookup" className="w-full text-left space-y-3">
        <h2 className="text-lg font-bold text-gray-900 dark:text-zinc-100">Lookup</h2>
        <p className="text-sm text-gray-500 dark:text-zinc-400">Select a workspace organization first.</p>
      </div>
    );
  }

  const counts: Record<OpsLookupSlider, number> = {
    sales: vm.filteredSales.length,
    orders: vm.filteredOrders.length,
    service: vm.filteredServices.length,
  };

  return (
    <div data-app-panel="OpsLookup" data-testid="ops-lookup-panel" className="w-full text-left space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-zinc-100">Lookup</h2>
          <p className="mt-1 max-w-3xl text-sm text-gray-600 dark:text-zinc-300">
            Find sales, manufacturing orders, and service jobs. Read-only — open Deals /
            Service Board to edit. Use <strong>Report a bug</strong> while viewing a row so triage
            gets the focused sale / order / service.
          </p>
        </div>
        <button
          type="button"
          data-testid="ops-lookup-refresh"
          className="rounded-md border border-gray-300 px-3 py-1.5 text-sm dark:border-zinc-600"
          onClick={() => void vm.reload()}
          disabled={vm.loading}
        >
          {vm.loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      <div className="flex flex-wrap gap-2" role="tablist" aria-label="Lookup sliders">
        {(["sales", "orders", "service"] as OpsLookupSlider[]).map((id) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={vm.slider === id}
            data-testid={`ops-lookup-slider-${id}`}
            className={tabBtn(vm.slider === id)}
            onClick={() => vm.setSlider(id)}
          >
            {SLIDER_LABEL[id]} ({counts[id]})
          </button>
        ))}
      </div>

      <label className="block max-w-xl text-sm text-gray-700 dark:text-zinc-300">
        Search
        <input
          type="search"
          data-testid="ops-lookup-search"
          className="mt-1 block w-full rounded border border-gray-300 bg-white px-2 py-1.5 dark:border-zinc-600 dark:bg-zinc-900"
          placeholder="Serial, customer, sale name, plant, id…"
          value={vm.query}
          onChange={(e) => vm.setQuery(e.target.value)}
        />
      </label>

      {vm.error ? (
        <p className="text-sm text-red-600 dark:text-red-400" role="alert">
          {vm.error}
        </p>
      ) : null}
      {vm.softWarning ? (
        <p className="text-sm text-amber-700 dark:text-amber-300" role="status">
          {vm.softWarning}
        </p>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="min-w-0 space-y-2" data-app-panel="OpsLookupList">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">
            {SLIDER_LABEL[vm.slider]} ({counts[vm.slider]})
          </h3>
          {vm.slider === "sales" ? (
            <OpsLookupSaleList
              rows={vm.filteredSales}
              unitsByDealId={vm.unitsByDealId}
              selectedId={vm.selectedSaleId}
              onSelect={vm.setSelectedSaleId}
            />
          ) : null}
          {vm.slider === "orders" ? (
            <OpsLookupOrderList
              rows={vm.filteredOrders}
              selectedId={vm.selectedOrderId}
              onSelect={vm.setSelectedOrderId}
            />
          ) : null}
          {vm.slider === "service" ? (
            <OpsLookupServiceList
              rows={vm.filteredServices}
              unitsById={vm.unitsById}
              selectedId={vm.selectedServiceId}
              onSelect={vm.setSelectedServiceId}
            />
          ) : null}
        </section>

        <section className="min-w-0" data-app-panel="OpsLookupDetail">
          {vm.slider === "sales" && vm.selectedSale ? (
            <OpsLookupSaleDetail
              sale={vm.selectedSale}
              linkedUnit={vm.unitsByDealId.get(vm.selectedSale.id) ?? null}
              cost={costs.rollup}
              costLoading={costs.loading}
              relatedServices={
                <OpsLookupRelatedServices
                  jobs={vm.relatedForSelectedSale}
                  onOpen={vm.jumpToService}
                />
              }
              onOpenOrder={vm.jumpToOrder}
            />
          ) : null}
          {vm.slider === "orders" && vm.selectedOrder ? (
            <OpsLookupOrderDetailConnected
              unit={vm.selectedOrder}
              plant={vm.plantById.get(vm.selectedOrder.plant_id) ?? null}
              linkedSale={
                vm.selectedOrder.ops_deal_id
                  ? (vm.sales.find((s) => s.id === vm.selectedOrder!.ops_deal_id) ?? null)
                  : null
              }
              cost={costs.rollup}
              costLoading={costs.loading}
              relatedServices={
                <OpsLookupRelatedServices
                  jobs={vm.relatedForSelectedOrder}
                  onOpen={vm.jumpToService}
                />
              }
              onOpenSale={vm.jumpToSale}
            />
          ) : null}
          {vm.slider === "service" && vm.selectedService ? (
            <OpsLookupServiceDetail
              job={vm.selectedService}
              linkedUnit={
                vm.selectedService.mfg_unit_id
                  ? (vm.unitsById.get(vm.selectedService.mfg_unit_id) ?? null)
                  : null
              }
              onOpenOrder={vm.jumpToOrder}
              onOpenSale={vm.jumpToSale}
            />
          ) : null}
          {vm.slider === "sales" && !vm.selectedSale ? (
            <p className="text-sm text-gray-500 dark:text-zinc-400">
              {vm.loading ? "Loading sales…" : "No sale selected."}
            </p>
          ) : null}
          {vm.slider === "orders" && !vm.selectedOrder ? (
            <p className="text-sm text-gray-500 dark:text-zinc-400">
              {vm.loading ? "Loading orders…" : "No order selected."}
            </p>
          ) : null}
          {vm.slider === "service" && !vm.selectedService ? (
            <p className="text-sm text-gray-500 dark:text-zinc-400">
              {vm.loading ? "Loading service…" : "No service job selected."}
            </p>
          ) : null}
        </section>
      </div>
    </div>
  );
}
